import { useTranslation } from '@thoughtspot/i18n';
import { Alert } from '@thoughtspot/radiant-react/widgets/alert';
import { ErrorCode } from '@thoughtspot/radiant-react/widgets/alert/alert-constants';
import { BannerAlert, BannerAlertType, } from '@thoughtspot/radiant-react/widgets/alert/banner/banner-alert';
import { Icon, IconColor, IconID, IconSize, } from '@thoughtspot/radiant-react/widgets/icon';
import { Input } from '@thoughtspot/radiant-react/widgets/input';
import { LoadingIndicator } from '@thoughtspot/radiant-react/widgets/loading-indicator';
import { Typography } from '@thoughtspot/radiant-react/widgets/typography';
import cx from 'classnames';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useGetGitConfig } from "../../../../services/version-control/version-control";
import { Horizontal, Vertical } from "../../../../utils/layouts";
import { TranslationKeys, } from '../../constant';
import BranchSelectionForm from '../branch-selection-specific-org/branch-selection-form';
import styles from '../configuration-modal.module.scss';
const BranchSelectionFormAllOrgs = ({ branches, configBranches, onBranchStateChange, branchSelectionsState, errorList, }) => {
    const { t } = useTranslation();
    const [orgFilterText, setOrgsFilterText] = useState('');
    const [orgActiveInd, setOrgActiveInd] = useState(0);
    const [configData, setConfigData] = useState([]);
    const [loading, setLoading] = useState(true);
    const configFetchParams = {
        offset: -1,
        batchsize: -1,
        name_pattern: '',
    };
    const { data: getConfigData, loading: configLoading, fetchGitConfig, } = useGetGitConfig(configFetchParams);
    const checkTSConfig = useMemo(() => configBranches[0].options.some(branch => branch.label === "ts_config_files"), [configBranches]);
    const currentBranchStateRef = useRef({
        enableVersionHistory: true,
        guidBranch: checkTSConfig ? "ts_config_files" : '',
    });
    useEffect(() => {
        if (getConfigData && !configLoading) {
            setLoading(false);
            setConfigData(getConfigData.data);
        }
        if (configLoading) {
            setLoading(true);
        }
    }, [getConfigData, configLoading]);
    useEffect(() => {
        setOrgActiveInd(0);
        fetchGitConfig({
            ...configFetchParams,
            name_pattern: orgFilterText,
        });
    }, [orgFilterText]);
    const handleBranchSelectionInputChange = () => {
        const { branch, enableVersionHistory, guidBranch, } = currentBranchStateRef.current;
        onBranchStateChange(configData[orgActiveInd].org.id.toString(), {
            commit_branch_name: branch ?? '',
            enable_version_history: enableVersionHistory,
            configuration_branch_name: guidBranch ?? '',
            org_identifier: configData[orgActiveInd].org.id.toString(),
        });
    };
    const filteredBranches = useMemo(() => {
        if (configData?.length <= orgActiveInd)
            return null;
        const filteredBranchesArray = branches[0].options?.map(branch => {
            const isDisabled = branchSelectionsState.some(selection => {
                return (selection.commit_branch_name === branch.label &&
                    selection.org_identifier !==
                        configData[orgActiveInd].org.id.toString());
            });
            return {
                ...branch,
                disabled: isDisabled,
            };
        });
        return [{ options: filteredBranchesArray }];
    }, [orgActiveInd, configData]);
    const handleOrgChangeClick = (ind) => {
        const existingConfig = branchSelectionsState.find(data => data.org_identifier === configData[ind].org.id.toString());
        if (existingConfig) {
            currentBranchStateRef.current = {
                branch: existingConfig.commit_branch_name,
                enableVersionHistory: existingConfig.enable_version_history,
                guidBranch: existingConfig.configuration_branch_name,
            };
        }
        else {
            currentBranchStateRef.current = {
                enableVersionHistory: true,
                guidBranch: checkTSConfig
                    ? "ts_config_files"
                    : '',
            };
        }
        setOrgActiveInd(ind);
    };
    const getErrorMessage = (orgId) => {
        const foundObject = errorList.find(obj => obj.orgId === orgId);
        return foundObject ? foundObject.message : '';
    };
    const renderAlertmessage = (errorMessage) => {
        return (React.createElement(BannerAlert, { className: cx(styles.errorBannerAlert), isVisible: !!errorMessage, message: errorMessage, showCloseButton: false, type: BannerAlertType.ERROR }));
    };
    return (React.createElement(LoadingIndicator.Global, { className: cx(styles.branchSelectionFormAllOrgs), hideLoader: !loading, "data-testid": "branch-selection-all-org-form" },
        React.createElement(Vertical, { className: cx(styles.leftSelectionPan), basis: "35%" },
            configData?.length > 9 && (React.createElement("div", null,
                React.createElement(Input.Text, { ariaLabel: t(TranslationKeys.VCS_FILTER_SEARCH_TEXT), placeholder: t(TranslationKeys.VCS_FILTER_SEARCH_TEXT), type: "text", value: orgFilterText, onChange: e => {
                        setOrgsFilterText(e.target.value);
                    }, prefixIcon: IconID.MAGNIFYING_GLASS, suffixIcon: orgFilterText ? IconID.CROSS : null, onSuffixIconClick: () => {
                        setOrgsFilterText('');
                    }, className: styles.orgFilterInput }))),
            React.createElement("div", { className: styles.allOrgLabelsListParent },
                React.createElement("div", { className: styles.allOrgLabelsList }, configData?.map((info, ind) => {
                    const isActive = ind === orgActiveInd;
                    const orgLabelVariant = isActive
                        ? 'content-label-subhead'
                        : 'body-normal';
                    return (React.createElement("div", { key: info.org.id, className: cx({
                            [styles.selectedOrgBox]: isActive,
                        }) },
                        React.createElement(Horizontal, { key: info.org.name, className: cx(styles.orgLabelBox, {
                                [styles.selectedOrgLabelBox]: isActive,
                            }), onClick: () => handleOrgChangeClick(ind) },
                            React.createElement(Typography, { variant: orgLabelVariant, ellipsis: { rows: 1 }, tooltipConfig: {
                                    dataTooltipContent: info.org.name,
                                    showTooltipOnlyOnEllipsis: false,
                                }, noMargin: true, wrapContent: true }, info.org.name),
                            getErrorMessage(info.org.id) && (React.createElement(Icon, { className: cx(styles.exclamationIcon), id: IconID.EXCLAMATION_POINT_CIRCLE, size: IconSize.LARGE, color: IconColor.RED })))));
                })))),
        configData?.length > orgActiveInd && (React.createElement(Vertical, { className: cx(styles.rightBranchSelection), basis: "65%" },
            renderAlertmessage(getErrorMessage(configData[orgActiveInd]?.org?.id)),
            React.createElement(Typography, { variant: "h5", noMargin: true }, configData[orgActiveInd].org.name),
            React.createElement(BranchSelectionForm, { branches: filteredBranches, configBranches: configBranches, formRef: currentBranchStateRef, isOfAllOrgType: true, onInputChange: handleBranchSelectionInputChange }))),
        React.createElement(Alert.Muted, { isVisible: !loading && configData?.length === 0, title: t(TranslationKeys.VCS_ADMIN_NO_MATCH_MSG), description: "", errorCode: ErrorCode.GLOBAL_DATA, "data-testid": "no-data-alert", global: true, imageVisible: true, className: styles.noMatchAlert })));
};
export default BranchSelectionFormAllOrgs;
//# sourceMappingURL=branch-selection-all-orgs-form.js.map